import React from 'react';
import { cn } from '../../shared/ui/utils';
import type { ResolvedPresenceStatus } from '../../types/presence';

interface PresenceDotProps {
  status: ResolvedPresenceStatus;
  /** Outer size in px (the SVG is square). Default 12. */
  size?: number;
  className?: string;
}

/**
 * SVG-based presence indicator dot.
 *
 * Available      – solid green circle
 * Busy           – solid red circle
 * Do Not Disturb – red circle with a white horizontal bar
 * Be Right Back  – solid amber circle
 * Away           – amber ring (outline only — suggests temporary absence)
 * Offline        – light-grey filled circle with a diagonal slash
 * In a Meeting   – solid amber circle with a white bar (set automatically)
 * On Site        – solid blue circle (set automatically)
 */
export function PresenceDot({ status, size = 12, className }: PresenceDotProps) {
  const c = size / 2;      // centre
  const r = c - 0.75;      // radius with a small gap so edges look clean

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('shrink-0', className)}
      aria-hidden="true"
    >
      {shape(status, c, r)}
    </svg>
  );
}

function shape(status: ResolvedPresenceStatus, c: number, r: number) {
  switch (status) {
    case 'available':
      return <circle cx={c} cy={c} r={r} fill="#22c55e" />;

    case 'busy':
      return <circle cx={c} cy={c} r={r} fill="#ef4444" />;

    case 'dnd':
      return (
        <g>
          <circle cx={c} cy={c} r={r} fill="#ef4444" />
          {/* White horizontal bar — the "minus / no-entry" signal */}
          <rect
            x={c - r * 0.62}
            y={c - r * 0.2}
            width={r * 1.24}
            height={r * 0.4}
            rx={r * 0.2}
            fill="white"
          />
        </g>
      );

    case 'be_right_back':
      return <circle cx={c} cy={c} r={r} fill="#f59e0b" />;

    case 'away':
      // Ring only — same colour as BRB but the outline distinguishes it
      return (
        <circle
          cx={c}
          cy={c}
          r={r - 0.75}
          stroke="#f59e0b"
          strokeWidth="1.5"
          fill="none"
        />
      );

    case 'offline':
      return (
        <g>
          <circle cx={c} cy={c} r={r} fill="#e2e8f0" />
          <line
            x1={c - r * 0.5}
            y1={c - r * 0.5}
            x2={c + r * 0.5}
            y2={c + r * 0.5}
            stroke="#94a3b8"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>
      );

    case 'in_meeting':
      return (
        <g>
          <circle cx={c} cy={c} r={r} fill="#f59e0b" />
          <rect
            x={c - r * 0.62}
            y={c - r * 0.2}
            width={r * 1.24}
            height={r * 0.4}
            rx={r * 0.2}
            fill="white"
          />
        </g>
      );

    case 'on_site':
      return <circle cx={c} cy={c} r={r} fill="#3b82f6" />;

    default:
      return <circle cx={c} cy={c} r={r} fill="#94a3b8" />;
  }
}
